import React, { useEffect, useState } from "react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { ActionButton } from "@fluentui/react/lib/Button";
import { ContextualMenu } from "@fluentui/react";
import { DBApplication, DimensionDetails } from "../interfaces/CoObject";
import { fetchChildrenDimension, fetchDimensions, mapDimensionToDimensionItem } from "../../api/object/Dimensions";
import { Dimension } from "../../api/models/dimension";
import { TreeDropDownInput } from "../coobjects/tree-pills-input/TreeInput";
import { fetchGroupUserDB } from "../../api/object/Form";
import { Toggle } from "@fluentui/react/lib/Toggle";
import {
  CollapsableCard,
  CollapsableCardDefaultHeaderComponent,
  CollapsableCardHeaderComponentProps,
} from "../layout/CollapsableCard";

export interface ParametersDocumentCardState {
  expandCard: boolean;
  availableDimensions: Dimension[] | undefined;
  selectedDimensionsItems: Dimension[];
  isContextualMenuOpen: boolean;
  loadedDbId: string | undefined;
}

export interface DocumentParameters {
  dimensions: DimensionDetails[];
  dbId: string;
}

export interface ParametersDocumentState {
  availableDbApplication: DBApplication[] | undefined;
  isContextualMenuOpen: boolean;
}

interface DocumentParametersProps {
  documentParameters: DocumentParameters[];
  updateDocumentParameters: (doc: DocumentParameters[], valid: boolean, modified: boolean) => void;
  canInherit: boolean;
  fatherDocumentParameters: DocumentParameters[] | undefined;
}

export const ParametersDocument: React.FunctionComponent<DocumentParametersProps> = (props) => {
  const [state, setState] = useState<ParametersDocumentState>({
    availableDbApplication: undefined,
    isContextualMenuOpen: false,
  });

  const { documentParameters, updateDocumentParameters, fatherDocumentParameters } = props;

  useEffect(() => {
    const getInitialValues = async () => {
      const dbs = await fetchGroupUserDB();
      setState((s) => ({
        ...s,
        availableDbApplication: dbs,
      }));
      updateDocumentParameters(documentParameters, true, false);
    };

    getInitialValues();
  }, []);

  const updateDimensionFromTree = (
    dimensionItem: Dimension,
    newDimensions: DimensionDetails[] | undefined,
    documentParameter: DocumentParameters
  ) => {
    const dimensionToAdd: DimensionDetails[] = newDimensions?.map((newDimension) => ({
      ...newDimension,
      dimensionOid: dimensionItem.oid,
      structureCode: dimensionItem.type,
      code: dimensionItem.code,
    }));

    const newDocumentParameter = documentParameters.map((p) => {
      if (p.dbId !== documentParameter.dbId) return p;
      const updatedDimensions = p.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid);
      updatedDimensions.push(...dimensionToAdd);
      return { ...p, dimensions: updatedDimensions };
    });

    updateDocumentParameters(newDocumentParameter, true, true);
  };

  return (
    <div className="ms-Grid-row">
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        {state.availableDbApplication?.length ? (
          state.availableDbApplication.map((dbApplication) => {
            const thisDocumentParametersInThisDb = documentParameters.find((p) => p.dbId === dbApplication.id);
            const inheritableDocumentParametersInThisDb = fatherDocumentParameters?.find(
              (p) => p.dbId === dbApplication.id
            );
            return (
              <ParametersDocumentCard
                key={dbApplication.id}
                inheritableDocumentParametersInThisDb={inheritableDocumentParametersInThisDb}
                updateDimensionFromTree={(dimensionItem, newDimensions) =>
                  updateDimensionFromTree(dimensionItem, newDimensions, thisDocumentParametersInThisDb)
                }
                dimensions={thisDocumentParametersInThisDb?.dimensions}
                selectedDbApplication={dbApplication}
                enableDbApplication={(enabled) => {
                  const newDocParams = documentParameters.filter((p) => p.dbId !== dbApplication.id) ?? [];
                  if (enabled) {
                    newDocParams.push({
                      dbId: inheritableDocumentParametersInThisDb?.dbId ?? dbApplication.id,
                      dimensions:
                        inheritableDocumentParametersInThisDb?.dimensions.map((d) => ({
                          ...d,
                          inherited: true,
                        })) ?? [],
                    });
                  }

                  updateDocumentParameters(newDocParams, true, true);
                }}
                canInherit={props.canInherit}
              />
            );
          })
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={"/assets/genericErrorIllustration.svg"}
              alt="No database associated with the current user"
              style={{
                width: "calc(100vw - 4rem)",
                maxHeight: "calc(100vh - 3rem)",
                objectFit: "contain",
              }}
            />
            <span
              style={{
                width: "100%",
                textAlign: "center",
                color: "var(--color-blue)",
                lineBreak: "anywhere",
              }}
            >
              {formatMessage("cof.documentproperties.parameters.error.nodatabase")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const getHeaderComponent = ({
  isExpanded,
  setExpanded,
  dimensions,
  onChangeToggle,
  selectedDbApplication,
}: CollapsableCardHeaderComponentProps & {
  dimensions: DimensionDetails[];
  onChangeToggle: (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
  selectedDbApplication: DBApplication;
}) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <Toggle checked={dimensions !== undefined} onChange={onChangeToggle} styles={{ root: { marginBottom: 0 } }} />
    <span style={{ height: "100%", textAlign: "center", flexGrow: 1 }}>{selectedDbApplication.desc}</span>
    {dimensions && <CollapsableCardDefaultHeaderComponent isExpanded={isExpanded} setExpanded={setExpanded} />}
  </div>
);

const ParametersDocumentCard: React.FunctionComponent<{
  selectedDbApplication: DBApplication;
  updateDimensionFromTree: (dimensionItem: Dimension, newDimensions: DimensionDetails[] | undefined) => void;
  dimensions: DimensionDetails[] | undefined;
  enableDbApplication: (enable: boolean) => void;
  inheritableDocumentParametersInThisDb: DocumentParameters | undefined;
  canInherit: boolean;
}> = ({
  selectedDbApplication,
  updateDimensionFromTree,
  dimensions,
  enableDbApplication,
  inheritableDocumentParametersInThisDb,
  canInherit,
}) => {
  const [state, setState] = useState<ParametersDocumentCardState>({
    expandCard: false,
    availableDimensions: undefined,
    isContextualMenuOpen: false,
    selectedDimensionsItems: [],
    loadedDbId: undefined,
  });

  const addDimensionLinkRef = React.useRef(null);

  useEffect(() => {
    if (selectedDbApplication.id !== state.loadedDbId && dimensions)
      fetchDimensions(selectedDbApplication.id, true).then((dimensionsDetails) => {
        const defaultSelectedDimensions = dimensionsDetails.filter(
          (d) => dimensions?.find((value) => value.dimensionOid === d.oid) !== undefined
        );
        setState((s) => ({
          ...s,
          loadedDbId: selectedDbApplication.id,
          availableDimensions: dimensionsDetails,
          selectedDimensionsItems: defaultSelectedDimensions,
        }));
      });
  }, [selectedDbApplication, dimensions]);

  const filteredDimensions = state.availableDimensions?.filter(
    (dimension) =>
      state.selectedDimensionsItems.find((presentItem) => dimension.desc === presentItem.desc) === undefined //not default and not previously selected
  );

  const onChangeToggle = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    enableDbApplication(checked);
    setState((s) => ({
      ...s,
      expandCard: checked ?? false,
      selectedDimensionsItems: checked ? s.selectedDimensionsItems : [],
    }));
  };

  function removeDimension(dimensionItem: Dimension) {
    updateDimensionFromTree(dimensionItem, undefined);
  }

  return (
    <CollapsableCard
      externalState={{
        isExpanded: state.expandCard,
        setExpanded: (expanded) => setState((s) => ({ ...s, expandCard: expanded })),
      }}
      headerComponent={({ isExpanded, setExpanded }) =>
        getHeaderComponent({
          setExpanded,
          isExpanded,
          dimensions,
          onChangeToggle,
          selectedDbApplication,
        })
      }
    >
      {state.selectedDimensionsItems.map((dimensionItem, index, arr) => {
        const thisDimensionsDetails = dimensions?.filter((d) => d.dimensionOid === dimensionItem.oid);
        return (
          <TreeDropDownInput
            key={dimensionItem.oid + "DropDown"}
            fetchRoot={async (oid) => {
              const items = await fetchChildrenDimension(selectedDbApplication.id, oid);
              return items.map((i) => mapDimensionToDimensionItem(i));
            }}
            fetchChildren={async (item) => {
              const items = await fetchChildrenDimension(selectedDbApplication.id, item.data.oid);
              return items.map((i) => mapDimensionToDimensionItem(i));
            }}
            dimensions={thisDimensionsDetails}
            canInherit={
              canInherit &&
              (inheritableDocumentParametersInThisDb?.dimensions ?? []).filter(
                (d) => d.dimensionOid === dimensionItem.oid
              )
            }
            inherited={thisDimensionsDetails?.[0]?.inherited}
            label={dimensionItem.desc}
            modalTitle={dimensionItem.desc}
            placeholder={formatMessage("cof.base.select")}
            rootOid={dimensionItem.oid}
            hierarchical={dimensionItem.hierarchical}
            multiSelectable={dimensionItem.multiSelectable}
            onChange={(newDimension) => {
              updateDimensionFromTree(dimensionItem, newDimension);
            }}
            onRemove={() => {
              removeDimension(dimensionItem);
              arr.splice(index, 1);
              setState((s) => ({
                ...s,
                userSelectedDimensionsItems: arr,
                isContextualMenuOpen: false,
                dimensions: dimensions.filter((d) => d.dimensionOid === dimensionItem.oid),
              }));
            }}
            dbId={selectedDbApplication?.id}
            isSearchAvailable={true}
          />
        );
      })}
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <ActionButton
            iconProps={{ iconName: "CircleAdditionSolid" }}
            disabled={!filteredDimensions?.length}
            onClick={() => {
              setState((s) => ({ ...s, isContextualMenuOpen: true }));
            }}
            style={{
              float: "left",
              marginBottom: "5px",
            }}
          >
            <b ref={addDimensionLinkRef} style={{ color: "var(--color-blue)" }}>
              {formatMessage("cof.base.adddimension")}
            </b>
          </ActionButton>
          {state.isContextualMenuOpen && (
            <ContextualMenu
              target={addDimensionLinkRef}
              items={filteredDimensions?.map((d) => ({
                key: d.oid,
                text: d.desc,
                onClick: () => {
                  setState((s) => ({
                    ...s,
                    isContextualMenuOpen: false,
                    selectedDimensionsItems: [...s.selectedDimensionsItems, d],
                  }));
                },
              }))}
              onDismiss={() => {
                setState((s) => ({
                  ...s,
                  isContextualMenuOpen: false,
                }));
              }}
            />
          )}
        </div>
      </div>
    </CollapsableCard>
  );
};
